import { AxiosRequestConfig } from 'axios';
import { HttpResponse, ajax } from '../../../../common/ajax';
import { config } from '../../config';
import { VoucherCodeTypes } from '../../enums';
import { CheckoutVoucherResponseModel } from '../../models/Checkout';
import { VoucherDetailsModel, VoucherItemModel } from '../../models/Vouchers';

interface IVoucherService {
  getVouchers: (
    queryString?: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<VoucherItemModel[]>>;
  getVoucherDetails: (
    productId: number,
    orderId: number,
    codeType: VoucherCodeTypes,
    codeIntId: number | null,
    codeStringId: string | null,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<VoucherDetailsModel>>;
  downloadVoucher: (
    productId: number,
    orderId: number,
    codeType: VoucherCodeTypes,
    codeIntId: number | null,
    codeStringId: string | null,
    filename: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<null>>;
  printVoucher: (
    productId: number,
    orderId: number,
    codeType: VoucherCodeTypes,
    codeIntId: number | null,
    codeStringId: string | null,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<null>>;
  emailVoucher: (
    productId: number,
    orderId: number,
    codeType: VoucherCodeTypes,
    codeIntId: number | null,
    codeStringId: string | null,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<null>>;
  checkoutVoucherOrder: (
    productId: number,
    options: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<CheckoutVoucherResponseModel>>;
  markVoucherUsage: (
    orderProductId: number,
    codeType: VoucherCodeTypes,
    codeIntId: number | null,
    codeStringId: string | null,
    isUsed: boolean,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
}

export const voucherService: IVoucherService = {
  getVouchers(queryString, axiosConfig) {
    return ajax.get<VoucherItemModel[]>(
      `${config.benefitApiUrl}/odata/vouchers?${queryString}`,
      axiosConfig,
    );
  },
  getVoucherDetails(
    productId,
    orderId,
    codeType,
    codeIntId,
    codeStringId,
    axiosConfig,
  ) {
    const codeIntIdParam = codeIntId !== null ? `&codeIntId=${codeIntId}` : '';
    const codeStringIdParam =
      codeStringId !== null
        ? `&codeStringId=${encodeURIComponent(codeStringId)}`
        : '';

    return ajax.get<VoucherDetailsModel>(
      `${config.benefitApiUrl}/vouchers?productId=${productId}&orderId=${orderId}&codeType=${codeType}${codeIntIdParam}${codeStringIdParam}`,
      axiosConfig,
    );
  },
  downloadVoucher(
    productId,
    orderId,
    codeType,
    codeIntId,
    codeStringId,
    filename,
    axiosConfig,
  ) {
    return ajax.downloadFile(
      `${config.benefitApiUrl}/vouchers/download?productId=${productId}&orderId=${orderId}&codeType=${codeType}&codeIntId=${codeIntId}&codeStringId=${codeStringId}`,
      filename,
      'pdf',
      axiosConfig,
    );
  },
  printVoucher(
    productId,
    orderId,
    codeType,
    codeIntId,
    codeStringId,
    axiosConfig,
  ) {
    return ajax.printFile(
      `${config.benefitApiUrl}/vouchers/download?productId=${productId}&orderId=${orderId}&codeType=${codeType}&codeIntId=${codeIntId}&codeStringId=${codeStringId}`,
      axiosConfig,
    );
  },
  emailVoucher(
    productId,
    orderId,
    codeType,
    codeIntId,
    codeStringId,
    axiosConfig,
  ) {
    return ajax.get(
      `${config.benefitApiUrl}/vouchers/email?productId=${productId}&orderId=${orderId}&codeType=${codeType}&codeIntId=${codeIntId}&codeStringId=${codeStringId}`,
      axiosConfig,
    );
  },
  checkoutVoucherOrder(productId, options, axiosConfig) {
    return ajax.post<CheckoutVoucherResponseModel>(
      `${config.benefitApiUrl}/vouchers/checkout`,
      { productId, options },
      axiosConfig,
    );
  },
  markVoucherUsage(
    orderProductId,
    codeType,
    codeIntId,
    codeStringId,
    isUsed,
    axiosConfig,
  ) {
    return ajax.post<boolean>(
      `${config.benefitApiUrl}/vouchers/mark-used`,
      { orderProductId, codeType, codeIntId, codeStringId, isUsed },
      axiosConfig,
    );
  },
};
